import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../../configurations/api';
import { httpRequest } from '../../redux/types';
import { campaignDeliverableOptionsUrl } from '../../configurations/api/url';
import type {
    IDeliverable,
    DeliverableType,
    DeliverablePlatform,
    DeliverableDuration,
} from '../../redux/buzzbridge/types';

interface OptionItem {
    value: string;
    label: string;
    platform?: string;
    direct_hire_only?: boolean;
}

interface DeliverableOptions {
    types: OptionItem[];
    platforms: OptionItem[];
    duration_ranges: OptionItem[];
}

const FALLBACK: DeliverableOptions = {
    types: [
        { value: 'instagram_post', label: 'Instagram Post', platform: 'instagram' },
        { value: 'instagram_reel', label: 'Instagram Reel', platform: 'instagram' },
        { value: 'instagram_story', label: 'Instagram Story', platform: 'instagram' },
        { value: 'tiktok_video', label: 'TikTok Video', platform: 'tiktok' },
        { value: 'tiktok_short', label: 'TikTok Short', platform: 'tiktok' },
        { value: 'youtube_short', label: 'YouTube Short', platform: 'youtube' },
        { value: 'youtube_video', label: 'YouTube Video', platform: 'youtube' },
        { value: 'twitter_post', label: 'Twitter / X Post', platform: 'twitter' },
        { value: 'facebook_post', label: 'Facebook Post', platform: 'facebook' },
        { value: 'ugc_video', label: 'UGC Video' },
        { value: 'ugc_photo', label: 'UGC Photo' },
        { value: 'vertical', label: 'Vertical Video' },
        { value: 'horizontal', label: 'Horizontal Video' },
    ],
    platforms: [
        { value: 'instagram', label: 'Instagram' },
        { value: 'tiktok', label: 'TikTok' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'twitter', label: 'Twitter' },
        { value: 'x', label: 'X' },
        { value: 'facebook', label: 'Facebook' },
    ],
    duration_ranges: [
        { value: 'n/a', label: 'N/A' },
        { value: '0-15s', label: '0 – 15 seconds' },
        { value: '15-30s', label: '15 – 30 seconds' },
        { value: '30s-1min', label: '30 seconds – 1 minute' },
        { value: '1min-3min', label: '1 – 3 minutes' },
        { value: '3min-5min', label: '3 – 5 minutes' },
        { value: '5min+', label: '5 minutes+' },
    ],
};

const DEFAULT_FORM = {
    type: '' as DeliverableType | '',
    platform: '' as DeliverablePlatform | '',
    duration_range: 'n/a' as DeliverableDuration,
    count: 1,
    notes: '',
    multiUploadEnabled: false,
    multiUploadCount: 2,
    multiUploadLabels: ['', ''] as string[],
};

interface Props {
    open: boolean;
    onClose: () => void;
    onAdd: (deliverable: IDeliverable) => void;
    existing?: IDeliverable;
    selectedPlatforms?: string[];
    isDirectHire?: boolean;
    /** When set, loads deliverable-options?campaign_type=… */
    campaignType?: 'standard' | 'direct_hire' | 'sync_upload' | 'commission';
}

const labelSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    mb: 0.75,
    color: '#9e9e9e',
};
const selectSx = {
    bgcolor: '#1e1e1e',
    color: '#fff',
    borderRadius: 2,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-icon': { color: '#9e9e9e' },
};
const menuProps = {
    PaperProps: {
        sx: {
            bgcolor: '#1e1e1e',
            color: '#fff',
            '& .MuiMenuItem-root:hover': { bgcolor: '#2e2e2e' },
        },
    },
};

export function DeliverablePickerModal({
    open,
    onClose,
    onAdd,
    existing,
    selectedPlatforms,
    isDirectHire = false,
    campaignType,
}: Props) {
    const [options, setOptions] = useState<DeliverableOptions>(FALLBACK);
    const [loading, setLoading] = useState(false);
    const formFromExisting = (d: IDeliverable) => ({
        type: d.type,
        platform: d.platform ?? ('' as DeliverablePlatform | ''),
        duration_range: d.duration_range,
        count: d.count,
        notes: d.notes ?? '',
        multiUploadEnabled: d.multiple_uploads?.enabled ?? false,
        multiUploadCount: d.multiple_uploads?.count ?? 2,
        multiUploadLabels: d.multiple_uploads?.labels?.length
            ? [...d.multiple_uploads.labels]
            : ['', ''],
    });

    const [form, setForm] = useState(() =>
        existing ? formFromExisting(existing) : { ...DEFAULT_FORM },
    );

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        const typeQuery = campaignType || (isDirectHire ? 'direct_hire' : undefined);
        const optionsUrl = typeQuery
            ? `${campaignDeliverableOptionsUrl}?campaign_type=${typeQuery}`
            : campaignDeliverableOptionsUrl;
        api(optionsUrl, httpRequest.GET, null)
            .then((res: any) => {
                const data = res?.data;
                if (data?.types && data?.platforms && data?.duration_ranges) {
                    setOptions(data as DeliverableOptions);
                }
            })
            .catch(() => {
                /* fall back silently */
            })
            .finally(() => setLoading(false));
    }, [open, isDirectHire, campaignType]);

    useEffect(() => {
        if (open) {
            setForm(existing ? formFromExisting(existing) : { ...DEFAULT_FORM });
        }
    }, [open, existing]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = <K extends keyof typeof form>(key: K, val: (typeof form)[K]) =>
        setForm(prev => ({ ...prev, [key]: val }));

    const handleTypeChange = (type: DeliverableType) => {
        const meta = options.types.find(d => d.value === type);
        setForm(prev => ({
            ...prev,
            type,
            platform: (meta?.platform as DeliverablePlatform) ?? prev.platform,
        }));
    };

    const hasPlatformFilter = selectedPlatforms && selectedPlatforms.length > 0;
    const filteredTypes = isDirectHire
        ? options.types.filter(d => d.value === 'vertical' || d.value === 'horizontal')
        : hasPlatformFilter
        ? options.types.filter(d => !d.platform || selectedPlatforms!.includes(d.platform))
        : options.types.filter(d => !d.direct_hire_only);
    const filteredPlatforms = hasPlatformFilter
        ? options.platforms.filter(p => selectedPlatforms!.includes(p.value))
        : options.platforms;

    const multiLabelsValid = !form.multiUploadEnabled ||
        (form.multiUploadLabels.slice(0, form.multiUploadCount).every(l => l.trim() !== ''));

    const canAdd = form.type !== '' && form.count >= 1 && (isDirectHire || form.platform !== '') && multiLabelsValid;

    const handleAdd = () => {
        if (!canAdd) return;
        onAdd({
            type: form.type as DeliverableType,
            ...(form.platform && { platform: form.platform as DeliverablePlatform }),
            duration_range: form.duration_range,
            count: form.count,
            ...(form.notes?.trim() && { notes: form.notes.trim() }),
            ...(form.multiUploadEnabled && {
                multiple_uploads: {
                    enabled: true,
                    count: form.multiUploadCount,
                    labels: form.multiUploadLabels.slice(0, form.multiUploadCount).map(l => l.trim()),
                },
            }),
        });
        onClose();
    };

    const handleMultiUploadCountChange = (newCount: number) => {
        setForm(prev => {
            const labels = [...prev.multiUploadLabels];
            while (labels.length < newCount) labels.push('');
            return { ...prev, multiUploadCount: newCount, multiUploadLabels: labels };
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { bgcolor: '#141414', borderRadius: 3, color: '#fff' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                    {existing ? 'Edit deliverable' : 'Add deliverable'}
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: '#9e9e9e' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} sx={{ color: '#ffc965' }} />
                    </Box>
                ) : (
                    <>
                        {/* Content type */}
                        <Box>
                            <Typography sx={labelSx}>Content type</Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    displayEmpty
                                    value={form.type}
                                    onChange={e => handleTypeChange(e.target.value as DeliverableType)}
                                    sx={selectSx}
                                    MenuProps={menuProps}>
                                    <MenuItem value="" disabled sx={{ color: '#666' }}>
                                        Select type…
                                    </MenuItem>
                                    {filteredTypes.map(d => (
                                        <MenuItem key={d.value} value={d.value}>
                                            {d.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Platform — optional for direct hire */}
                        {!isDirectHire && (
                            <Box>
                                <Typography sx={labelSx}>Platform</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        displayEmpty
                                        value={form.platform}
                                        onChange={e => set('platform', e.target.value as DeliverablePlatform)}
                                        sx={selectSx}
                                        MenuProps={menuProps}>
                                        <MenuItem value="" disabled sx={{ color: '#666' }}>
                                            Select platform…
                                        </MenuItem>
                                        {filteredPlatforms.map(p => (
                                            <MenuItem key={p.value} value={p.value}>
                                                {p.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        {/* Duration */}
                        <Box>
                            <Typography sx={labelSx}>Duration</Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={form.duration_range}
                                    onChange={e => set('duration_range', e.target.value as DeliverableDuration)}
                                    sx={selectSx}
                                    MenuProps={menuProps}>
                                    {options.duration_ranges.map(d => (
                                        <MenuItem key={d.value} value={d.value}>
                                            {d.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Notes */}
                        <Box>
                            <Typography sx={labelSx}>
                                Notes{' '}
                                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                                    (optional)
                                </span>
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                rows={2}
                                placeholder="e.g. Must feature the product in first 3 seconds"
                                value={form.notes}
                                onChange={e => set('notes', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: '#1e1e1e',
                                        borderRadius: 2,
                                        color: '#fff',
                                        fontSize: 13,
                                        '& fieldset': { border: 'none' },
                                        '&.Mui-focused fieldset': { border: '1px solid #ffc96540' },
                                    },
                                }}
                            />
                        </Box>

                        {/* Multiple uploads */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography sx={labelSx}>Multiple uploads</Typography>
                                    <Typography sx={{ fontSize: 12, color: '#666', mt: 0.25 }}>
                                        Require creators to upload multiple versions of this deliverable
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={form.multiUploadEnabled}
                                    onChange={e => set('multiUploadEnabled', e.target.checked)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#ffc965' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ffc96550' },
                                    }}
                                />
                            </Box>

                            {form.multiUploadEnabled && (
                                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {/* Count stepper */}
                                    <Box>
                                        <Typography sx={labelSx}>Number of uploads</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                                            {[2, 3, 4, 5].map(n => (
                                                <Button
                                                    key={n}
                                                    size="small"
                                                    onClick={() => handleMultiUploadCountChange(n)}
                                                    sx={{
                                                        minWidth: 36,
                                                        px: 1,
                                                        borderRadius: 1.5,
                                                        fontWeight: 700,
                                                        fontSize: 13,
                                                        textTransform: 'none',
                                                        ...(form.multiUploadCount === n
                                                            ? { bgcolor: '#ffc965', color: '#1a1a1a' }
                                                            : { bgcolor: '#1e1e1e', color: '#9e9e9e' }),
                                                    }}>
                                                    {n}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>

                                    {/* Labels — one per slot */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography sx={labelSx}>Labels (required)</Typography>
                                        {Array.from({ length: form.multiUploadCount }).map((_, i) => (
                                            <TextField
                                                key={i}
                                                size="small"
                                                fullWidth
                                                placeholder={`Upload ${i + 1} label (e.g. English)`}
                                                value={form.multiUploadLabels[i] ?? ''}
                                                onChange={e => {
                                                    const labels = [...form.multiUploadLabels];
                                                    labels[i] = e.target.value;
                                                    set('multiUploadLabels', labels);
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: '#1e1e1e',
                                                        borderRadius: 2,
                                                        color: '#fff',
                                                        fontSize: 13,
                                                        '& fieldset': { border: 'none' },
                                                        '&.Mui-focused fieldset': { border: '1px solid #ffc96540' },
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 1.5, pt: 0.5 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={onClose}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: '#333',
                                    color: '#9e9e9e',
                                    '&:hover': { borderColor: '#555', bgcolor: 'transparent' },
                                }}>
                                Cancel
                            </Button>
                            <Button
                                fullWidth
                                disabled={!canAdd}
                                onClick={handleAdd}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    background: canAdd ? 'linear-gradient(90deg, #ffc965, #ff8c00)' : undefined,
                                    bgcolor: !canAdd ? '#2a2a2a' : undefined,
                                    color: canAdd ? '#1a1a1a' : '#555',
                                    '&:hover': { opacity: 0.9 },
                                    '&.Mui-disabled': { opacity: 0.4 },
                                }}>
                                {existing ? 'Save' : 'Add'}
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
